import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FolderKanban, Trash2, ArrowRight, Plus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useWorkingModel } from "@/hooks/useWorkingModel";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader } from "@/components/PageParts";
import DeliverableState from "@/components/DeliverableState";
import PlanSVG from "@/components/PlanSVG";
import { Button, Card, Empty } from "@/components/ui";

export default function Projects() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  const navigate = useNavigate();
  const [, setWm] = useWorkingModel();
  const { data, loading, error, reload } = useDeliverable(() => api.editorListProjects(), []);
  const items = data?.items || [];

  async function open(p) {
    try {
      const full = await api.editorGetProject(p.id);
      setWm({ model: full.model, kind: p.kind || "villa", floors: full.generation_params?.floors || 1, slab_system: "hordi_regular", title: full.name });
      navigate("/editor");
    } catch (e) {
      toast.error(String(e.message || e));
    }
  }

  async function del(p, e) {
    e.stopPropagation();
    if (!window.confirm(isAr ? "حذف المشروع؟" : "Delete project?")) return;
    try {
      await api.editorDeleteProject(p.id);
      toast.success(isAr ? "تم الحذف" : "Deleted");
      reload();
    } catch (ex) {
      toast.error(String(ex.message || ex));
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow={isAr ? "المشاريع" : "Projects"}
        title={isAr ? "مشاريعي" : "My projects"}
        right={<Button onClick={() => navigate("/studio")} data-testid="new-project-btn"><Plus size={16} /> {isAr ? "مشروع جديد" : "New project"}</Button>}
      />
      <DeliverableState loading={loading} error={error} isAr={isAr}>
        {items.length === 0 ? (
          <Empty icon={FolderKanban} title={isAr ? "لا توجد مشاريع بعد" : "No projects yet"} hint={isAr ? "ابدأ بتوليد مخطط من الاستوديو" : "Generate a plan from the Studio to get started"} action={<Button onClick={() => navigate("/studio")}>{isAr ? "افتح الاستوديو" : "Open Studio"}</Button>} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <Card key={p.id} className="cursor-pointer p-0 overflow-hidden transition-transform hover:-translate-y-1" onClick={() => open(p)} data-testid={`project-${p.id}`}>
                <div className="border-b border-border bg-gradient-to-br from-slate-800 to-slate-900">
                  {p.model?.walls?.length ? <PlanSVG model={p.model} w={340} h={200} showLabels={false} showPlot={false} /> : (
                    <div className="grid h-[200px] place-items-center">
                      <FolderKanban className="text-primary/40" size={48} strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between p-3">
                  <div className="min-w-0">
                    <div className="truncate font-black">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.kind || p.status}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={(e) => del(p, e)} className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-destructive/15 hover:text-red-400" data-testid={`delete-${p.id}`}><Trash2 size={15} /></button>
                    <ArrowRight size={16} className="text-primary" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </DeliverableState>
    </div>
  );
}
